<!--
 * @Version      : V1.0
 * @Date         : 2026-08-14
 * @Description  : 电费代付新增/编辑
-->
<template>
  <div class="electricity-pay">
    <div class="top-toolbar">
      <el-select
        clearable
        v-model="form.contractReceivePaymentId"
        filterable
        :disabled="!isEditable"
        :loading="selectLoading"
        placeholder="请选择关联合同"
        class="contract-select"
        @change="onContractChange"
      >
        <el-option
          v-for="item in receivableContractOption"
          :key="item.id"
          :label="item.contractName"
          :value="item.id"
        />
      </el-select>
      <el-button
        v-if="form.contractReceivePaymentId"
        type="text"
        @click="openViewProject(form.contractReceivePaymentId, '2')"
      >查看</el-button>
    </div>

    <el-row :gutter="16" type="flex" class="content-row">
      <el-col :span="16">
        <div class="ele-card main-card">
          <div class="section-header">
            <span class="section-dot"></span>
            <span class="section-title">统一单价</span>
          </div>
          <div class="unit-price-box">
            统一收取单价 U =
            <span class="unit-price-value">{{ formatMoney(unitPrice, 4) }}</span>
            元/kWh
            <span class="unit-price-tip">（请在「电费收取」页面修改）</span>
          </div>
          <el-button
            v-if="isEditable"
            type="primary"
            plain
            icon="el-icon-plus"
            class="mt-10"
            :disabled="!form.contractReceivePaymentId"
            @click="openRecharge"
          >充值电量</el-button>

          <div class="section-header mt-18">
            <span class="section-dot"></span>
            <span class="section-title">已确认收费</span>
            <el-tag size="small" type="primary" class="ml-8">{{ confirmedFeeList.length }} 笔</el-tag>
          </div>
          <el-table v-if="confirmedFeeList.length" :data="confirmedFeeList" border size="small" class="confirmed-table">
            <el-table-column label="名称" min-width="160">
              <template slot-scope="scope">
                {{ scope.row.collectionName || '—' }}
                <el-tag size="mini" :type="scope.row.electricityPaymentId ? 'success' : 'primary'" class="ml-8">{{ scope.row.electricityPaymentId ? '代付审批中' : '待代付' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="金额(元)" align="right" width="120">
              <template slot-scope="scope">{{ formatMoney(scope.row.chargingPriceTotal) }}</template>
            </el-table-column>
            <el-table-column label="电量(kWh)" align="right" width="120">
              <template slot-scope="scope">{{ formatMoney(scope.row.electricityConsumption) }}</template>
            </el-table-column>
          </el-table>
          <div v-else class="empty-tip">暂无已确认的收费记录，请前往「电费收取」页面操作。</div>
          <div v-if="confirmedFeeList.length" class="confirmed-total">
            合计：{{ formatMoney(derived.totalAmount) }} 元 / {{ formatMoney(derived.totalKwh) }} kWh
          </div>

          <div class="section-header mt-18">
            <span class="section-dot"></span>
            <span class="section-title">代付电量</span>
          </div>
          <div class="prepay-row">
            <div class="prepay-field">
              <div class="field-label required">成本单价 C (元/kWh)</div>
              <el-input-number
                v-model="form.costPrice"
                :min="0"
                :precision="4"
                :controls="false"
                :disabled="!isEditable"
                placeholder="0.45"
                @change="onPrepayChange"
              />
            </div>
            <div class="prepay-field">
              <div class="field-label required">代付电量 (kWh)</div>
              <el-input-number
                v-model="form.prepayKwh"
                :min="0"
                :precision="2"
                :controls="false"
                :disabled="!isEditable"
                placeholder="0"
                @change="onPrepayChange"
              />
            </div>
            <div class="prepay-field">
              <div class="field-label">代付金额 (元)</div>
              <el-input :value="prepayAmountText" disabled />
            </div>
          </div>

          <div class="detail-panel">
            <div class="detail-toggle" @click="detailExpanded = !detailExpanded">
              <i :class="detailExpanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              批次详情（{{ detailExpanded ? '收起' : '展开填写' }}）
            </div>
            <div class="detail-body" v-show="detailExpanded">
              <div class="detail-fields">
                <div class="detail-field">
                  <div class="detail-label required">电费合同付款名称</div>
                  <el-input v-model="form.paymentName" :disabled="!isEditable" placeholder="如：XX合同付款" maxlength="200" />
                </div>
                <div class="detail-field">
                  <div class="detail-label required">楼盘信息</div>
                  <el-input v-model="form.buildingInformation" :disabled="!isEditable" placeholder="如：XX小区/写字楼" maxlength="200" />
                </div>
                <div class="detail-field">
                  <div class="detail-label required">机房信息</div>
                  <el-input v-model="form.computerRoomInformation" :disabled="!isEditable" placeholder="如：A栋B1机房" maxlength="200" />
                </div>
                <div class="detail-field">
                  <div class="detail-label required">抄表日期</div>
                  <el-date-picker
                    v-model="form.meterReadDate"
                    type="date"
                    value-format="yyyy-MM-dd"
                    :disabled="!isEditable"
                    placeholder="请选择抄表日期"
                    style="width: 100%"
                  />
                </div>
                <div class="detail-field full">
                  <div class="detail-label required">收款方单位名称</div>
                  <el-input v-model="form.payeeName" :disabled="!isEditable" placeholder="如：XX电力有限公司" maxlength="200" />
                </div>
                <div class="detail-field">
                  <div class="detail-label required">开户行</div>
                  <el-input v-model="form.bankName" :disabled="!isEditable" placeholder="如：XX银行XX支行" maxlength="200" />
                </div>
                <div class="detail-field">
                  <div class="detail-label required">开户行账号</div>
                  <el-input v-model="form.bankAccount" :disabled="!isEditable" placeholder="请输入银行账号" maxlength="50" />
                </div>
              </div>
              <div class="meter-block">
                <div class="sub-title">电表信息汇总（来自待代付收取记录）</div>
                <el-table v-if="meterSummary.length" :data="meterSummary" border size="small">
                  <el-table-column label="来源收费" prop="feeName" min-width="120" show-overflow-tooltip />
                  <el-table-column label="电表位置" prop="electricityMeterLocation" min-width="120" />
                  <el-table-column label="电表号" prop="electricityMeterNo" min-width="120" />
                  <el-table-column label="上期底表数" align="right" width="110">
                    <template slot-scope="scope">{{ formatMoney(scope.row.lastIssueBaseNum) }}</template>
                  </el-table-column>
                  <el-table-column label="本期底表数" align="right" width="110">
                    <template slot-scope="scope">{{ formatMoney(scope.row.currentIssueBaseNum) }}</template>
                  </el-table-column>
                </el-table>
                <div v-else class="empty-tip">暂无待代付收取记录的电表信息</div>
              </div>
            </div>
          </div>

          <div class="submit-wrap" v-if="isEditable">
            <el-button type="success" icon="el-icon-check" :loading="loading" :disabled="loading" @click="submit(1)">
              提交代付（结算）
            </el-button>
            <div class="prepay-hint" v-if="carryForward">⚠ 上期结转：{{ formatMoney(carryForward) }} kWh，已计入可用电量。</div>
          </div>

          <div class="batch-history" v-if="batchList.length">
            <div class="section-header">
              <span class="section-dot cyan"></span>
              <span class="section-title">批次历史</span>
              <el-tag size="small" type="info" class="ml-8">{{ batchList.length }} 批</el-tag>
            </div>
            <div v-for="(b, idx) in reversedBatches" :key="b.id || idx" class="batch-card">
              <div class="batch-head" @click="toggleBatch(b)">
                <span>批次 {{ batchList.length - idx }} <span class="batch-time">{{ b.time }}</span></span>
                <span class="batch-toggle">{{ b._expanded ? '收起' : '展开' }}</span>
              </div>
              <div class="batch-summary">
                <div><span class="bs-k">收费笔数</span><span class="bs-v">{{ (b.feeIds || []).length }} 笔</span></div>
                <div><span class="bs-k">收取电量</span><span class="bs-v">{{ formatMoney(b.totalKwh) }} kWh</span></div>
                <div><span class="bs-k">收取金额</span><span class="bs-v">{{ formatMoney(b.totalAmount) }} 元</span></div>
                <div><span class="bs-k">代付电量</span><span class="bs-v">{{ formatMoney(b.totalPrepay) }} kWh</span></div>
                <div><span class="bs-k">最大可购</span><span class="bs-v">{{ formatMoney(b.totalMaxPurch) }} kWh</span></div>
                <div><span class="bs-k">代付金额</span><span class="bs-v">{{ formatMoney(b.totalPrepayCost) }} 元</span></div>
                <div>
                  <span class="bs-k">结转到下期</span>
                  <span class="bs-v" :class="Number(b.carryForward) >= 0 ? 'is-green' : 'is-red'">{{ formatMoney(b.carryForward) }} kWh</span>
                </div>
                <div><span class="bs-k">利润</span><span class="bs-v">{{ formatMoney(b.profit) }} 元</span></div>
              </div>
              <div class="batch-detail" v-show="b._expanded">
                <div v-if="b.paymentName">合同付款名称：{{ b.paymentName }}</div>
                <div v-if="b.propertyInfo">楼盘信息：{{ b.propertyInfo }}</div>
                <div v-if="b.serverRoomInfo">机房信息：{{ b.serverRoomInfo }}</div>
                <div v-if="b.meterReadingDate">抄表日期：{{ b.meterReadingDate }}</div>
                <div v-if="b.payeeName">收款方：{{ b.payeeName }}</div>
                <div v-if="b.bankName || b.bankAccount">开户行：{{ b.bankName }} {{ b.bankAccount }}</div>
              </div>
            </div>
          </div>

          <div class="section-header mt-18">
            <span class="section-dot"></span>
            <span class="section-title">垫付事件</span>
            <el-button
              v-if="isEditable"
              type="primary"
              plain
              size="mini"
              icon="el-icon-plus"
              class="ml-8"
              :disabled="!form.contractReceivePaymentId"
              @click="openAdvance"
            >新增垫付</el-button>
          </div>
          <div v-if="advanceList.length" class="advance-list">
            <div v-for="(item, index) in advanceList" :key="item.id || index" class="advance-item">
              <div class="advance-main">
                <div>
                  <strong>{{ item.reason || '（未命名垫付）' }}</strong>
                  <span class="advance-time">{{ item.time }}</span>
                  <el-tag size="mini" type="danger" class="ml-8">已计入成本</el-tag>
                </div>
                <div class="advance-meta">
                  垫付电量：{{ formatMoney(item.kwh) }} kWh ｜ 垫付金额：{{ formatMoney(item.amount) }} 元
                  <span v-if="item.payee"> ｜ 垫付人：{{ item.payee }}</span>
                </div>
              </div>
              <el-button v-if="isEditable" type="text" class="danger-text" @click="removeAdvance(index)">删除</el-button>
            </div>
          </div>
          <div v-else class="empty-tip">暂无垫付事件</div>
        </div>
      </el-col>

      <el-col :span="8">
        <div class="ele-card indicator-card">
          <div class="section-header">
            <span class="section-dot"></span>
            <span class="section-title">实时指标</span>
          </div>
          <div class="indicator-grid">
            <div class="indicator-item">
              <div class="indicator-name">上期结转电量</div>
              <div class="indicator-value" :class="carryForward > 0 ? 'is-green' : (carryForward < 0 ? 'is-red' : '')">
                {{ formatMoney(carryForward) }} <span class="unit">kWh</span>
              </div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">充值电量（累计）</div>
              <div class="indicator-value is-green">{{ formatMoney(rechargeKwh) }} <span class="unit">kWh</span></div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">本期收取电量 ΣkWh</div>
              <div class="indicator-value is-blue">{{ formatMoney(derived.totalKwh) }} <span class="unit">kWh</span></div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">可用电量（收取+结转）</div>
              <div class="indicator-value is-blue">{{ formatMoney(derived.availableKwh) }} <span class="unit">kWh</span></div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">最大可购电量</div>
              <div class="indicator-value is-orange">{{ formatMoney(derived.totalMaxPurch) }} <span class="unit">kWh</span></div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">收取金额</div>
              <div class="indicator-value is-blue">{{ formatMoney(derived.totalAmount) }} <span class="unit">元</span></div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">本期代付电量</div>
              <div class="indicator-value">{{ formatMoney(derived.prepayTotalKwh) }} <span class="unit">kWh</span></div>
            </div>
            <div class="indicator-item">
              <div class="indicator-name">代付金额</div>
              <div class="indicator-value is-green">{{ formatMoney(derived.totalPrepayCost) }} <span class="unit">元</span></div>
            </div>
          </div>
          <div class="indicator-tip" :class="alertType">
            <i :class="alertIcon"></i>
            {{ alertText }}
          </div>
        </div>
      </el-col>
    </el-row>

    <view-other-details ref="viewProjectRef"></view-other-details>

    <!-- 充值电量 -->
    <el-dialog
      title="电费充值"
      :visible.sync="rechargeVisible"
      width="480px"
      :close-on-click-modal="false"
      append-to-body
      custom-class="pay-dialog"
    >
      <p class="dialog-hint">
        统一单价 U = {{ formatMoney(unitPrice, 4) }} 元/kWh，输入电量或金额自动互算，充值将从当前合同可用电量和上期结转中扣除。
      </p>
      <el-form label-width="110px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="充值电量(kWh)">
              <el-input-number
                v-model="rechargeForm.kwh"
                :min="0"
                :precision="2"
                :controls="false"
                class="full-input"
                @change="onRechargeKwhChange"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="充值金额(元)">
              <el-input-number
                v-model="rechargeForm.amount"
                :min="0"
                :precision="2"
                :controls="false"
                class="full-input"
                @change="onRechargeAmtChange"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button @click="rechargeVisible = false">取消</el-button>
        <el-button type="success" @click="confirmRecharge">确认充值</el-button>
      </div>
    </el-dialog>

    <!-- 新增垫付 -->
    <el-dialog
      title="新增垫付"
      :visible.sync="advanceVisible"
      width="520px"
      :close-on-click-modal="false"
      append-to-body
      custom-class="pay-dialog"
    >
      <p class="dialog-hint">
        打开时「垫付电量」已自动填为 本次代付电量 − 最大可购电量（即代付超支、需垫付补差的那部分电量）；垫付金额按本批次成本单价 C 由电量自动换算，将全额计入成本，从实际利润扣除。
      </p>
      <el-form :model="advanceForm" label-width="120px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="垫付事由" required>
              <el-input v-model="advanceForm.reason" placeholder="如：临时抢修电费" maxlength="200" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="垫付电量(kWh)" required>
              <el-input-number
                v-model="advanceForm.kwh"
                :min="0"
                :precision="2"
                :controls="false"
                class="full-input"
                @change="onAdvanceKwhChange"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="垫付金额(元)" required>
              <el-input-number
                v-model="advanceForm.amount"
                :min="0"
                :precision="2"
                :controls="false"
                class="full-input"
                @change="onAdvanceAmtChange"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="垫付人" required>
              <el-input v-model="advanceForm.payee" placeholder="如：张三" maxlength="50" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button @click="advanceVisible = false">取消</el-button>
        <el-button type="success" @click="confirmAdvance">确认垫付</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryPageForLedgerListApi } from '@/api/receivableContract‌/index'
import { querylistForReceivePaymentApi } from '@/api/electricityFeeCollection/index'
import { queryListNoPaymentApi, addFinElectricityPaymentApi, editFinElectricityPaymentApi, reAddFinElectricityPaymentApi } from '@/api/electricityFeePay/index'
import viewOtherDetails from '@/components/viewOtherDetails/index.vue'
import { transformStr } from '@/utils/index'

export default {
  name: 'ElectricityFeePayAdd',
  components: { viewOtherDetails },
  props: {
    status: {
      type: String,
      default: 'add'
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectLoading: false,
      loading: false,
      receivableContractOption: [],
      confirmedFeeList: [],
      batchList: [],
      advanceList: [],
      carryForward: 0,
      rechargeKwh: 0,
      advanceConsumedKwh: 0,
      unitPrice: 0.62,
      detailExpanded: false,
      rechargeVisible: false,
      advanceVisible: false,
      rechargeSync: '',
      advanceSync: '',
      form: this.createEmptyForm(),
      rechargeForm: { kwh: undefined, amount: undefined },
      advanceForm: { reason: '', kwh: undefined, amount: undefined, payee: '' }
    }
  },
  computed: {
    isEditable() {
      return this.status !== 'view'
    },
    derived() {
      const U = Number(this.unitPrice) || 0
      const C = Number(this.form.costPrice) || 0
      const unpaid = this.confirmedFeeList
      const totalKwh = unpaid.reduce((s, f) => s + (Number(f.electricityConsumption) || 0), 0)
      const totalAmount = unpaid.reduce((s, f) => s + (Number(f.chargingPriceTotal) || 0), 0)
      const advanceKwh = this.advanceList.reduce((s, a) => s + (Number(a.kwh) || 0), 0)
      const advanceKwhEff = Math.max(0, advanceKwh - (Number(this.advanceConsumedKwh) || 0))
      const availableKwh = totalKwh + (Number(this.carryForward) || 0)
      const pkRaw = this.form.prepayKwh
      const prepayKwhEmpty = pkRaw === '' || pkRaw == null
      const pk = prepayKwhEmpty ? 0 : (Number(pkRaw) || 0)
      const totalMaxPurch = C > 0 ? availableKwh * U / C + advanceKwhEff : advanceKwhEff
      const totalPrepayCost = C * pk
      const over = pk > totalMaxPurch + 1e-9
      const profit = totalKwh * (U - C)
      return {
        U, C, totalKwh, totalAmount, availableKwh, totalMaxPurch, totalPrepayCost,
        prepayTotalKwh: pk, prepayKwhEmpty, over, hasUnpaid: unpaid.length > 0, profit, advanceKwh, advanceKwhEff
      }
    },
    prepayAmountText() {
      const val = this.derived.totalPrepayCost
      return val > 0 ? this.formatMoney(val) : ''
    },
    meterSummary() {
      const list = []
      this.confirmedFeeList.forEach(f => {
        (f.collectionItemList || []).forEach(m => {
          list.push({
            feeName: f.collectionName || '',
            electricityMeterLocation: m.electricityMeterLocation || '',
            electricityMeterNo: m.electricityMeterNo || '',
            lastIssueBaseNum: m.lastIssueBaseNum,
            currentIssueBaseNum: m.currentIssueBaseNum
          })
        })
      })
      return list
    },
    reversedBatches() {
      return this.batchList.slice().reverse()
    },
    alertType() {
      const d = this.derived
      if (d.over) return 'is-danger'
      if (!d.hasUnpaid || d.prepayKwhEmpty || d.prepayTotalKwh < 0) return 'is-warn'
      return 'is-ok'
    },
    alertIcon() {
      return this.alertType === 'is-ok' ? 'el-icon-success' : (this.alertType === 'is-danger' ? 'el-icon-error' : 'el-icon-warning')
    },
    alertText() {
      const d = this.derived
      if (!d.hasUnpaid) return '暂无已确认收费记录，请先前往「电费收取」页面添加并确认收费。'
      if (d.over) return `代付超支！已代付 ${this.formatMoney(d.prepayTotalKwh)} kWh ＞ 最大可购 ${this.formatMoney(d.totalMaxPurch)} kWh`
      if (d.prepayTotalKwh < 0) return '代付电量不能为负数。'
      if (d.prepayKwhEmpty) return '请填写代付电量。'
      if (d.prepayTotalKwh === 0) return '代付电量为 0：将按收取电量计利润（不实际代付），可直接提交结算。'
      const pct = d.totalMaxPurch ? (d.prepayTotalKwh / d.totalMaxPurch * 100) : 0
      return `可代付，已代付 ${this.formatMoney(pct, 1)}%（最大可购），预计利润 ${this.formatMoney(d.profit)} 元。`
    }
  },
  watch: {
    rowData: {
      handler(val) {
        if (val && Object.keys(val).length) this.initForm(val)
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.getLedgerContractList()
  },
  methods: {
    transformStr,
    formatMoney(val, digits = 2) {
      const num = Number(val)
      if (val === '' || val == null || isNaN(num)) return '0'
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: digits })
    },
    createEmptyForm() {
      return {
        id: undefined,
        contractReceivePaymentId: '',
        costPrice: 0.45,
        prepayKwh: undefined,
        paymentName: '',
        buildingInformation: '',
        computerRoomInformation: '',
        meterReadDate: '',
        payeeName: '',
        bankName: '',
        bankAccount: ''
      }
    },
    getLedgerContractList(v = '') {
      this.selectLoading = true
      queryPageForLedgerListApi({ params: { contractName: v } }).then(res => {
        this.selectLoading = false
        if (res.code == 200) this.receivableContractOption = res.data || []
      }).catch(() => { this.selectLoading = false })
    },
    onContractChange(id) {
      this.confirmedFeeList = []
      this.unitPrice = 0.62
      if (!id) return
      this.loadConfirmedFees(id)
    },
    loadConfirmedFees(contractReceivePaymentId) {
      const req = queryListNoPaymentApi({
        contractReceivePaymentId,
        params: { name: '' }
      })
      req.then(res => {
        if (res.code == 200 && Array.isArray(res.data) && res.data.length) {
          this.confirmedFeeList = res.data.filter(item =>
            item.collectionStatus == 2 && item.ifPayment != 1
          )
          this.syncUnitPrice()
          return
        }
        return querylistForReceivePaymentApi({ contractReceivePaymentId }).then(r2 => {
          if (r2.code == 200) {
            const list = r2.data || []
            this.confirmedFeeList = list.filter(item =>
              item.collectionStatus == 2 && item.ifPayment != 1
            )
            this.syncUnitPrice()
          }
        })
      }).catch(() => {
        querylistForReceivePaymentApi({ contractReceivePaymentId }).then(r2 => {
          if (r2.code == 200) {
            const list = r2.data || []
            this.confirmedFeeList = list.filter(item =>
              item.collectionStatus == 2 && item.ifPayment != 1
            )
            this.syncUnitPrice()
          }
        })
      })
    },
    syncUnitPrice() {
      const first = this.confirmedFeeList.find(item => item.chargingPrice != null && item.chargingPrice !== '')
      if (first) this.unitPrice = Number(first.chargingPrice) || 0.62
    },
    initForm(data = {}) {
      if (this.status === 'add') {
        this.form = this.createEmptyForm()
        this.advanceList = []
        this.batchList = []
        this.carryForward = Number(data.carryForward) || 0
        this.rechargeKwh = Number(data.rechargeKwh) || 0
        return
      }
      this.form = {
        ...this.createEmptyForm(),
        id: data.id,
        contractReceivePaymentId: data.contractReceivePaymentId || '',
        costPrice: data.originalPrice || data.costPrice || 0.45,
        prepayKwh: data.electricityConsumption || data.prepayKwh,
        paymentName: data.paymentName || '',
        buildingInformation: data.buildingInformation || '',
        computerRoomInformation: data.computerRoomInformation || '',
        meterReadDate: data.meterReadDate || '',
        payeeName: data.payeeName || data.receiveUnit || '',
        bankName: data.bankName || data.openBank || '',
        bankAccount: data.bankAccount || ''
      }
      this.unitPrice = Number(data.chargingPrice) || 0.62
      this.carryForward = Number(data.carryForward) || 0
      this.rechargeKwh = Number(data.rechargeKwh) || 0
      this.advanceList = data.advancePayments || data.advanceList || []
      this.batchList = (data.batches || []).map(item => ({ ...item, _expanded: false }))
      if (this.form.contractReceivePaymentId) this.loadConfirmedFees(this.form.contractReceivePaymentId)
    },
    onPrepayChange() {},
    toggleBatch(item) {
      this.$set(item, '_expanded', !item._expanded)
    },
    openRecharge() {
      if (!this.form.contractReceivePaymentId) {
        this.$message.warning('请先选择关联合同')
        return
      }
      this.rechargeForm = { kwh: undefined, amount: undefined }
      this.rechargeSync = ''
      this.rechargeVisible = true
    },
    onRechargeKwhChange(val) {
      if (this.rechargeSync === 'amt') return
      this.rechargeSync = 'kwh'
      const U = Number(this.unitPrice) || 0
      this.rechargeForm.amount = U > 0 && val >= 0 ? Number((val * U).toFixed(2)) : undefined
      this.$nextTick(() => { this.rechargeSync = '' })
    },
    onRechargeAmtChange(val) {
      if (this.rechargeSync === 'kwh') return
      this.rechargeSync = 'amt'
      const U = Number(this.unitPrice) || 0
      this.rechargeForm.kwh = U > 0 && val >= 0 ? Number((val / U).toFixed(2)) : undefined
      this.$nextTick(() => { this.rechargeSync = '' })
    },
    confirmRecharge() {
      const kwh = Number(this.rechargeForm.kwh)
      const U = Number(this.unitPrice) || 0
      if (!kwh || kwh <= 0) {
        this.$message.warning('请输入有效的充值电量（或金额）')
        return
      }
      this.$confirm(
        `确认充值 ${this.formatMoney(kwh)} kWh（约 ${this.formatMoney(kwh * U)} 元）？\n充值将从当前合同可用电量和上期结转中扣除。`,
        '电费充值',
        { type: 'warning' }
      ).then(() => {
        this.carryForward = (Number(this.carryForward) || 0) - kwh
        this.rechargeKwh = (Number(this.rechargeKwh) || 0) + kwh
        this.rechargeVisible = false
        this.$message.success('充值成功')
      }).catch(() => {})
    },
    openAdvance() {
      const d = this.derived
      const diff = (d.prepayTotalKwh || 0) - (d.totalMaxPurch || 0)
      const advKwh = diff > 0 ? Number(diff.toFixed(2)) : undefined
      const cost = Number(this.form.costPrice) || 0
      this.advanceForm = {
        reason: '',
        payee: '',
        kwh: advKwh,
        amount: cost > 0 && advKwh > 0 ? Number((advKwh * cost).toFixed(2)) : undefined
      }
      this.advanceSync = ''
      this.advanceVisible = true
    },
    onAdvanceKwhChange(val) {
      if (this.advanceSync === 'amt') return
      this.advanceSync = 'kwh'
      const cost = Number(this.form.costPrice) || 0
      this.advanceForm.amount = cost > 0 && val >= 0 ? Number((val * cost).toFixed(2)) : undefined
      this.$nextTick(() => { this.advanceSync = '' })
    },
    onAdvanceAmtChange(val) {
      if (this.advanceSync === 'kwh') return
      this.advanceSync = 'amt'
      const cost = Number(this.form.costPrice) || 0
      this.advanceForm.kwh = cost > 0 && val >= 0 ? Number((val / cost).toFixed(2)) : undefined
      this.$nextTick(() => { this.advanceSync = '' })
    },
    confirmAdvance() {
      const reason = (this.advanceForm.reason || '').trim()
      const payee = (this.advanceForm.payee || '').trim()
      const kwh = Number(this.advanceForm.kwh)
      const amount = Number(this.advanceForm.amount)
      if (!reason) {
        this.$message.warning('请填写垫付事由')
        return
      }
      if (!kwh || kwh <= 0) {
        this.$message.warning('请填写垫付电量')
        return
      }
      if (!amount || amount <= 0) {
        this.$message.warning('请填写有效的垫付金额')
        return
      }
      if (!payee) {
        this.$message.warning('请填写垫付人')
        return
      }
      this.advanceList.push({
        id: 'AP' + Date.now(),
        time: new Date().toLocaleString('zh-CN'),
        reason,
        kwh,
        amount,
        payee
      })
      this.advanceVisible = false
      this.$message.success('已新增垫付')
    },
    removeAdvance(index) {
      const item = this.advanceList[index]
      this.$confirm(`确认删除垫付事件「${item.reason || ''}」？`, '提示', { type: 'warning' })
        .then(() => { this.advanceList.splice(index, 1) })
        .catch(() => {})
    },
    async submit(status = 1) {
      if (!this.validateForm()) return
      const d = this.derived
      this.loading = true
      try {
        const params = this.buildParams(status, d)
        if (this.status === 'edit') {
          await editFinElectricityPaymentApi(params)
        } else if (this.status === 'reAdd') {
          await reAddFinElectricityPaymentApi(params)
        } else {
          await addFinElectricityPaymentApi(params)
        }
        this.$message.success('操作成功')
        this.loading = false
        this.$emit('queryData')
        this.$emit('success')
      } catch (e) {
        this.loading = false
      }
    },
    validateForm() {
      const d = this.derived
      if (!this.form.contractReceivePaymentId) {
        this.$message.warning('请选择关联合同')
        return false
      }
      if (!d.hasUnpaid) {
        this.$message.warning('没有已确认收费记录，请先前往「电费收取」页面确认收费后再提交')
        return false
      }
      if (d.prepayKwhEmpty) {
        this.$message.warning('请先填写代付电量')
        return false
      }
      if (d.prepayTotalKwh < 0) {
        this.$message.warning('代付电量不能为负数')
        return false
      }
      if (!(Number(this.form.costPrice) > 0)) {
        this.$message.warning('请填写有效的成本单价 C（须大于 0）')
        return false
      }
      const required = [
        ['paymentName', '电费合同付款名称'],
        ['buildingInformation', '楼盘信息'],
        ['computerRoomInformation', '机房信息'],
        ['meterReadDate', '抄表日期'],
        ['payeeName', '收款方单位名称'],
        ['bankName', '开户行'],
        ['bankAccount', '开户行账号']
      ]
      for (let i = 0; i < required.length; i++) {
        const [key, label] = required[i]
        if (!String(this.form[key] || '').trim()) {
          this.detailExpanded = true
          this.$message.warning(`请补全批次详情：${label}`)
          return false
        }
      }
      if (d.over) {
        this.$message.warning(`代付超支！${this.formatMoney(d.prepayTotalKwh)} kWh ＞ 最大可购 ${this.formatMoney(d.totalMaxPurch)} kWh`)
        return false
      }
      return true
    },
    buildParams(submitStatus, d) {
      const first = this.confirmedFeeList[0] || {}
      const params = {
        contractReceivePaymentId: this.form.contractReceivePaymentId,
        electricityCollectionId: first.id,
        finElectricityCollectionIds: this.confirmedFeeList.map(item => item.id).join(','),
        finElectricityCollectionCount: this.confirmedFeeList.length,
        paymentName: this.form.paymentName,
        buildingInformation: this.form.buildingInformation,
        computerRoomInformation: this.form.computerRoomInformation,
        meterReadDate: this.form.meterReadDate,
        payeeName: this.form.payeeName,
        bankName: this.form.bankName,
        bankAccount: this.form.bankAccount,
        originalPrice: this.form.costPrice,
        chargingPrice: this.unitPrice,
        electricityConsumption: this.form.prepayKwh,
        totalElectricityCollection: this.derived.totalKwh,
        originalPriceTotal: d.totalPrepayCost,
        originalPriceTotalCn: this.transformStr(d.totalPrepayCost),
        chargingPriceTotal: d.totalAmount,
        chargingPriceTotalCn: this.transformStr(d.totalAmount),
        profit: d.profit,
        profitCn: this.transformStr(d.profit),
        paymentItemList: this.meterSummary,
        paymentStatus: submitStatus,
        carryForward: this.carryForward,
        rechargeKwh: this.rechargeKwh,
        advancePayments: this.advanceList
      }
      if (this.form.id) params.id = this.form.id
      return params
    },
    clearForm() {
      this.form = this.createEmptyForm()
      this.confirmedFeeList = []
      this.advanceList = []
      this.batchList = []
      this.carryForward = 0
      this.rechargeKwh = 0
      this.unitPrice = 0.62
    },
    openViewProject(id, correlationType) {
      this.$refs.viewProjectRef.viewProject(id, correlationType)
    }
  }
}
</script>

<style scoped lang="scss">
.electricity-pay {
  padding: 10px 0;

  .top-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;

    .contract-select {
      width: 280px;
    }
  }

  .content-row {
    align-items: stretch;
    margin-bottom: 16px;

    ::v-deep > .el-col {
      display: flex;
      flex-direction: column;
    }

    .ele-card {
      flex: 1;
      width: 100%;
      margin-bottom: 0;
    }
  }

  .ele-card {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    margin-bottom: 16px;
    padding: 16px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .05);
  }

  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .section-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #409eff;
      margin-right: 8px;

      &.cyan {
        background: #22d3ee;
      }
    }

    .section-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }
  }

  .unit-price-box {
    padding: 12px 14px;
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    font-size: 14px;
    color: #303133;

    .unit-price-value {
      color: #409eff;
      font-weight: 700;
      font-size: 16px;
    }

    .unit-price-tip {
      margin-left: 8px;
      font-size: 12px;
      color: #909399;
    }
  }

  .confirmed-total {
    margin-top: 8px;
    text-align: right;
    font-size: 13px;
    font-weight: 600;
    color: #409eff;
  }

  .prepay-row {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;

    .prepay-field {
      flex: 1;
      min-width: 0;

      .el-input,
      .el-input-number {
        width: 100%;
      }
    }
  }

  .field-label,
  .detail-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 6px;

    &.required::before {
      content: '*';
      color: #f56c6c;
      margin-right: 2px;
    }
  }

  .detail-panel {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 12px;
  }

  .detail-toggle {
    padding: 10px 14px;
    background: #f5f7fa;
    color: #409eff;
    font-size: 13px;
    cursor: pointer;

    i {
      margin-right: 4px;
    }
  }

  .detail-body {
    padding: 14px;
  }

  .detail-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    .detail-field {
      width: calc(50% - 6px);

      &.full {
        width: 100%;
      }

      .el-input,
      .el-input-number,
      .el-date-picker {
        width: 100%;
      }
    }
  }

  .meter-block {
    margin-top: 14px;
  }

  .sub-title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .submit-wrap {
    margin: 12px 0 8px;
  }

  .prepay-hint {
    margin-top: 8px;
    font-size: 12px;
    color: #e6a23c;
  }

  .batch-card {
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 10px;
    background: #fafafa;
  }

  .batch-head {
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;

    .batch-time,
    .batch-toggle {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }
  }

  .batch-summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    font-size: 12px;

    .bs-k {
      display: block;
      color: #909399;
      margin-bottom: 2px;
    }

    .bs-v {
      font-weight: 600;
      color: #303133;

      &.is-green { color: #67c23a; }
      &.is-red { color: #f56c6c; }
    }
  }

  .batch-detail {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #dcdfe6;
    font-size: 12px;
    color: #606266;
    line-height: 1.8;
  }

  .advance-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .advance-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 12px;
  }

  .advance-time {
    margin-left: 8px;
    font-size: 12px;
    color: #909399;
    font-weight: 400;
  }

  .advance-meta {
    margin-top: 6px;
    font-size: 13px;
    color: #606266;
  }

  .indicator-card {
    display: flex;
    flex-direction: column;
    height: 100%;

    .indicator-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      flex: 1;
      align-content: start;
    }

    .indicator-item {
      border: 1px solid #ebeef5;
      border-radius: 6px;
      padding: 12px;
      background: #fafafa;

      .indicator-name {
        font-size: 12px;
        color: #909399;
        margin-bottom: 8px;
      }

      .indicator-value {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        word-break: break-all;

        .unit {
          font-size: 12px;
          font-weight: 400;
        }

        &.is-green { color: #67c23a; }
        &.is-blue { color: #409eff; }
        &.is-orange { color: #e6a23c; }
        &.is-red { color: #f56c6c; }
      }
    }

    .indicator-tip {
      margin-top: 14px;
      padding: 10px 12px;
      border-radius: 6px;
      font-size: 13px;
      line-height: 1.5;

      i { margin-right: 4px; }

      &.is-ok {
        border: 1px solid #b3e19d;
        background: #f0f9eb;
        color: #67c23a;
      }

      &.is-warn {
        border: 1px solid #f5dab1;
        background: #fdf6ec;
        color: #e6a23c;
      }

      &.is-danger {
        border: 1px solid #fbc4c4;
        background: #fef0f0;
        color: #f56c6c;
      }
    }
  }

  .mt-10 { margin-top: 10px; }
  .mt-18 { margin-top: 18px; }
  .ml-8 { margin-left: 8px; }
  .danger-text { color: #f56c6c; }

  .empty-tip {
    text-align: center;
    color: #909399;
    font-size: 13px;
    padding: 16px 0;
  }
}
</style>

<style lang="scss">
.pay-dialog {
  .dialog-hint {
    margin: 0 0 14px;
    font-size: 13px;
    color: #909399;
    line-height: 1.6;
  }

  .full-input,
  .el-input-number {
    width: 100%;
  }
}
</style>
